import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "When I make a promise to myself (routine, habit, boundary), I usually…",
    options: [
      "Follow through most of the time",
      "Try, but often delay or reschedule",
      "Drop it easily",
    ],
  },
  {
    question: "When honesty feels uncomfortable, I tend to…",
    options: [
      "Still speak my truth respectfully",
      "Soften or adjust the truth",
      "Avoid or hide it",
    ],
  },
  {
    question: "My daily choices generally feel…",
    options: [
      "Aligned with what I believe is right for me",
      "Aligned sometimes, compromised at other times",
      "Mostly driven by convenience or pressure",
    ],
  },
  {
    question: "When I make a mistake, my first instinct is to…",
    options: [
      "Take responsibility and correct it",
      "Justify or explain it",
      "Avoid accountability",
    ],
  },
  {
    question: "Even when no one is watching, I usually…",
    options: [
      "Act the same way I would if someone were",
      "Bend rules occasionally",
      "Take shortcuts",
    ],
  },
  {
    question: "When something goes against my values, I usually…",
    options: [
      "Speak up or step away",
      "Feel uncomfortable but stay quiet",
      "Go along with it",
    ],
  },
  {
    question: "I keep commitments I make to others…",
    options: ["Consistently", "Most of the time", "Only when it’s easy"],
  },
  {
    question: "When I realise I’m wrong, I usually…",
    options: [
      "Admit it openly",
      "Acknowledge it internally",
      "Defend myself",
    ],
  },
  {
    question: "My decisions are influenced by…",
    options: [
      "My values and long-term peace",
      "Situations and emotions",
      "Fear of consequences or opinions",
    ],
  },
  {
    question: "At the end of the day, when I reflect on my actions, I usually feel…",
    options: [
      "At peace with myself",
      "Mixed — some pride, some discomfort",
      "Uneasy or disconnected",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/integrity.jpg"
        alt="Integrity Assessment"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/90
          via-black/35
          to-transparent
        "
      />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              backdrop-blur-xl
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            SELF ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Integrity Assessment
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-4
            "
          >
            Discover how closely your actions,
            decisions, and habits align with
            your values and inner truth.
          </p>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
            "
          >
            Answer honestly based on how you
            typically behave, especially when
            situations are difficult or uncomfortable.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);
/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let title = "";
  let message = "";

  if (totalScore >= 15) {
    title = "🧭 Strong Inner Integrity";

    message =
      "Your actions generally align with your values and beliefs. This consistency creates trust, self-respect, and a deeper sense of inner peace. Continue honouring your truth, especially in everyday decisions.";
  } else if (totalScore >= 8) {
    title = "🌱 Situational Integrity";

    message =
      "You value honesty and alignment, but external pressures, emotions, or circumstances may occasionally influence your actions. Strengthening daily awareness and commitments can help create greater consistency.";
  } else {
    title = "🌧️ Integrity Needs Attention";

    message =
      "You may be experiencing frequent compromises between what you believe and how you act. This isn't a flaw—it's an opportunity to reconnect with your values and rebuild trust in yourself through small consistent actions.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Assessment Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/90
            via-black/35
            to-transparent
          "
        />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                backdrop-blur-xl
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Integrity Score: {totalScore} / 20
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {message}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const IntegrityQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default IntegrityQuiz;
